import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@plato/ui/primitives/tooltip';
import { cn } from '@plato/ui/util/classes';
import { useRef, useState, type ReactNode, type RefObject } from 'react';
import { useResizeObserver } from '../../lib/hooks/use-resize-observer.ts';
import Text, { type TextProps } from './text.tsx';

/**
 * Returns a ref + a boolean that's true when the referenced element overflows its rendered box
 * (single-line truncate OR multi-line clamp). Used to gate a tooltip so it only pops when
 * characters are actually hidden.
 */
function useIsCropped<T extends HTMLElement>(): readonly [RefObject<T | null>, boolean] {
    const ref = useRef<T>(null);
    const [cropped, setCropped] = useState(false);

    useResizeObserver({
        ref,
        onResize: () => {
            const el = ref.current;
            if (el) {
                setCropped(el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth);
            }
        },
    });

    return [ref, cropped] as const;
}

type TruncatedTextProps = {
    text: string;
    size?: TextProps['size'];
    color?: TextProps['color'];
    className?: string;
};

/**
 * Truncating text that only pops a tooltip with the full string when the text is actually cut off.
 */
export function TruncatedText({ text, size, color, className }: TruncatedTextProps): ReactNode {
    const [ref, cropped] = useIsCropped<HTMLSpanElement>();
    return (
        <TooltipProvider delay={700}>
            <Tooltip>
                <TooltipTrigger
                    render={
                        <Text ref={ref} size={size} color={color} className={cn('truncate', className)} />
                    }
                >
                    {text}
                </TooltipTrigger>
                {cropped && <TooltipContent>{text}</TooltipContent>}
            </Tooltip>
        </TooltipProvider>
    );
}
